/**
 * This module handles mapping url routes to the applications tabbing mechanism
 * and is exposed as a React hook.
 * There are three types of tabs that can be opened:
 * 
 * - An admin tab e.g services, tenant settings
 * - A flow canvas tab
 * - A page editor tab
 * 
 * An admin tab uses the following url structure: /:tenantid/:tabname?tab=:tabkey&page_name=:pagename
 * If the tab can be opened multiple times then a tab query parameter is appended to the url to make it unique
 * and therefore trigger the Router to re-render the application. If the tab is to open a new page editor,
 * then a page name query parameter is also included.
 * 
 * A flow canvas tab uses the following url structure: /:tenantid/flows/:flowid
 * 
 * A page editor tab uses the following url structure: /:tenantid/pages/:pageid
 */

import { useEffect, useRef } from 'react';
import { useMatch, useLocation, navigate } from '@reach/router';
import { tabDetailsObjects } from '../config/admin';
import { parse } from 'query-string';
import { SYSTEM_ROLES } from '../constants';

import EditorActions from '../actions/editor';
import FlowSources from '../sources/flow';
import SharedSource from '../sources/shared';
import FlowActions from '../actions/flow';
import GraphActions from '../actions/graph';

/**
 * 
 * @param {String} id 
 * @param {String} adminTenantId 
 * @param {String} authenticationToken 
 * @param {Array} editors Editor objects inside the Alt state store (these represent tabs in the application)
 * @description Calls an alt action that either opens a new flow tab or sets an
 * existing tab as active.
 */
const openFlowTab = async (id, adminTenantId, authenticationToken, editors) => {

    // We need the flows developer name to display in the tab itself...
    const { developerName } = await FlowSources.getLatest(
        id,
        adminTenantId,
        authenticationToken,
    );

    const flowTabExists = editors.find(editor => editor.type === 'flow' && editor.flowId === id);

    // If the tab is already open then we don't want to call these
    // actions, else the canvas state will be wiped.
    if (!flowTabExists) {
        FlowActions.open(id);
        GraphActions.setLoading(id);
        GraphActions.get(
            id,
            adminTenantId,
            authenticationToken,
        );
    }

    EditorActions.openFlow(
        id,
        developerName,
    );
};

/**
 * 
 * @param {String} id 
 * @param {String} adminTenantId 
 * @param {String} authenticationToken
 * @description Calls an alt action that either opens a new page tab or sets an
 * existing tab as active.
 */
const openPageTab = async (id, adminTenantId, authenticationToken) => {
    const response = await SharedSource.get(id, 'page', adminTenantId, authenticationToken);
    const parsedResponse = JSON.parse(response);
    EditorActions.openPageLayout(
        parsedResponse.id,
        parsedResponse.developerName,
    );
};

/**
 * 
 * @param {String} tabname e.g values, services etc
 * @param {Object} queryParams Key value pairs representing query parameters found in the url
 * @description Calls an alt action that either opens a new admin tab or sets an
 * existing tab as active.
 */
const openTab = async (tabname, queryParams, role) => {

    if (tabname === 'new_page') {
        EditorActions.newPageLayout(queryParams.tab, queryParams.page_name, queryParams.page_id);
    } else {
        const tabs = tabDetailsObjects;
        tabs.tenant = {
            label: 'Tenant',
            name: 'tenant',
            slug: 'tenant',
            icon: 'glyphicon-tree-deciduous',
            permissions: [SYSTEM_ROLES.administrator.developerName]
        };

        tabs.organizations = {
            label: 'Organization',
            name: 'organization',
            slug: 'organization',
            icon: 'glyphicon-briefcase',
            permissions: [SYSTEM_ROLES.administrator.developerName]
        };

        const tabDetails = Object.values(tabs).find(t => t.slug === (tabname || 'flows'));

        if (!tabDetails) {
            throw Error(`The tab named ${tabname} is not recognised`);
        }

        if (!tabDetails.permissions.includes(role)) {
            throw Error(`You do not have permissions to view the ${tabname} tab.`);
        } 
    
        // Extract the tab key from the url to be passed to the alt
        // action. This is to determine which tab to set as active
        // if the tab can be opened multiple times e.g players, macros.
        if (queryParams.tab) {
            tabDetails.key = queryParams.tab;
        }
    
        EditorActions.openEditor(
            tabDetails,
        );
    }
};

/**
 * 
 * @param {String} adminTenantId 
 * @param {String} authenticationToken 
 * @param {Array} editors Editor objects inside the Alt state store (these represent tabs in the application)
 * @param {Function} onError 
 */
const useTabRouting = (adminTenantId, authenticationToken, editors, onError, role) => {
    const previousTenantRef = useRef();
    const location = useLocation();

    const queryParams = parse(location.search);

    const tenantMatch = useMatch('/:tenantid/*');
    const currentTenantId = tenantMatch ? tenantMatch.tenantid : null;

    const tabMatch = useMatch('/:tenantid/:tabname');
    const tabname = tabMatch ? tabMatch.tabname : null;

    const flowMatch = useMatch('/:tenantid/flows/:flowid');
    const flowId = flowMatch ? flowMatch.flowid : null;

    const pageMatch = useMatch('/:tenantid/pages/:pageid');
    const pageId = pageMatch ? pageMatch.pageid : null;

    const previousTenant = previousTenantRef.current;

    const onTabOpen = (func, redirectTo) => {
        func.catch(({ message }) => {
            onError({
                type: 'error',
                message,
                isPersistent: true,
            });
            navigate(`/${currentTenantId}/${redirectTo}`, { replace: true });
        });
    };

    useEffect(() => {

        // We need to have a reference the previous tenant ID
        // as there are conditions where hooks should not be executed
        // if the tenant ID from the previous execution does not match the current.
        previousTenantRef.current = currentTenantId;
    });

    // Tab to open whenever navigating into a new tenant
    useEffect(() => {
        EditorActions.closeAll(false);

        if (tabname) {
            onTabOpen(openTab(tabname, queryParams, role), 'flows');
        }

        if (flowId) {
            onTabOpen(openFlowTab(flowId, adminTenantId, authenticationToken, editors), 'flows');
        }

        if (pageId) {
            onTabOpen(openPageTab(pageId, adminTenantId, authenticationToken), 'pages');
        }

    }, [authenticationToken]);

    // Tab to open/set as active whenever navigating to an admin tab
    useEffect(() => {
        if (currentTenantId === previousTenant && !flowMatch && !pageMatch) {
            onTabOpen(openTab(tabname, queryParams, role), 'flows');
        }
    }, [tabname, queryParams.tab]);

    // Tab to open/set as active whenever navigating to a flow tab
    useEffect(() => {
        if (flowId && currentTenantId === previousTenant) {
            onTabOpen(openFlowTab(flowId, adminTenantId, authenticationToken, editors), 'flows');
        }
    }, [flowId]);

    // Tab to open/set as active whenever navigating to a page tab
    useEffect(() => {
        if (pageId && currentTenantId === previousTenant) {
            onTabOpen(openPageTab(pageId, adminTenantId, authenticationToken), 'pages');
        }
    }, [pageId]);
};

export default useTabRouting;
